
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

interface ServerStatus {
  queue_available: number;
  active_tasks: number;
  server_load: 'low' | 'medium' | 'high' | 'critical';
  estimated_wait_time?: string;
  last_check?: number;
}

interface ServerStatusContextType {
  serverStatus: ServerStatus | null;
  isLoading: boolean;
  checkServerCapacity: () => Promise<ServerStatus | null>;
  getCapacityWarning: (status: ServerStatus | null) => string | null;
  shouldBlockRequests: (status: ServerStatus | null) => boolean;
}

const ServerStatusContext = createContext<ServerStatusContextType | undefined>(undefined);

export const useServerStatus = () => {
  const context = useContext(ServerStatusContext);
  if (context === undefined) {
    throw new Error('useServerStatus must be used within a ServerStatusProvider');
  }
  return context;
};

export const ServerStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [serverStatus, setServerStatus] = useState<ServerStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastCheck, setLastCheck] = useState<number>(0);
  const requestInProgress = useRef(false);

  const checkServerCapacity = useCallback(async (): Promise<ServerStatus | null> => {
    // Prevent duplicate requests
    if (requestInProgress.current) {
      return serverStatus;
    }

    const now = Date.now();
    const cacheTime = 60000; // 60 seconds cache - longer since we're only checking when needed
    
    // Use cached result if available and recent
    if (now - lastCheck < cacheTime && serverStatus) {
      return serverStatus;
    }

    requestInProgress.current = true;
    setIsLoading(true);
    
    try {
      console.log('Server status check at:', new Date().toISOString());
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch('https://flaskapp-f6g5.onrender.com/system-status', {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        console.warn('Server status check failed, assuming medium load');
        const assumedStatus: ServerStatus = {
          queue_available: 3,
          active_tasks: 5,
          server_load: 'medium',
          estimated_wait_time: '2-3 minutes',
          last_check: now
        };
        setServerStatus(assumedStatus);
        setLastCheck(now);
        return assumedStatus;
      }
      
      const status: ServerStatus = await response.json();
      
      // More lenient load classification - only block in truly extreme cases
      if (status.queue_available === 0 && status.active_tasks > 15) {
        status.server_load = 'critical';
      } else if (status.queue_available === 0) {
        status.server_load = 'high';
      } else if (status.active_tasks > 10) {
        status.server_load = 'medium';
      } else {
        status.server_load = 'low';
      }
      
      status.last_check = now;
      setServerStatus(status);
      setLastCheck(now);
      return status;
      
    } catch (error) {
      console.warn('Server status check failed:', error);
      
      // More optimistic fallback - assume medium load instead of critical
      const mediumStatus: ServerStatus = {
        queue_available: 2,
        active_tasks: 8,
        server_load: 'medium',
        estimated_wait_time: '2-3 minutes',
        last_check: now
      };
      setServerStatus(mediumStatus);
      setLastCheck(now);
      return mediumStatus;
      
    } finally {
      setIsLoading(false);
      requestInProgress.current = false;
    }
  }, [serverStatus, lastCheck]);

  const getCapacityWarning = useCallback((status: ServerStatus | null): string | null => {
    if (!status) return null;
    
    switch (status.server_load) {
      case 'critical':
        return 'Server is experiencing very high load. Requests may take 5-10 minutes and could potentially fail.';
      case 'high':
        return 'High server load detected. Your request may take 3-5 minutes to complete.';
      case 'medium':
        return 'Moderate server load. Your request may take 2-3 minutes to complete.';
      default:
        return null;
    }
  }, []);

  const shouldBlockRequests = useCallback((status: ServerStatus | null): boolean => {
    // Only block in extreme cases - queue full AND very high active tasks
    return status?.server_load === 'critical' && status?.queue_available === 0 && status?.active_tasks > 15;
  }, []);

  // Remove the automatic initial check - only check when actually needed

  return (
    <ServerStatusContext.Provider
      value={{
        serverStatus,
        isLoading,
        checkServerCapacity,
        getCapacityWarning,
        shouldBlockRequests
      }}
    >
      {children}
    </ServerStatusContext.Provider>
  );
};
